<script>
import messages from '@/mixins/messages'
import { sum } from '@/util'
import ReservationResource from '@/resources/reservation'
import PaymentMethod from './PaymentMethod.vue'
import HeaderImage from './HeaderImage.vue'
import TermsOfServiceLink from './TermsOfServiceLink.vue'
import ApexClubPitch from './ApexClubPitch.vue'
import RelatedImage from './RelatedImage.vue'
import CalendarResource from '@/resources/calendar'
import ChargeResource from '@/resources/charge'
import VoucherResource from '@/resources/voucher'

export default {
    name: 'ReservationBook',
    components: {
        PaymentMethod,
        HeaderImage,
        TermsOfServiceLink,
        RelatedImage,
        ApexClubPitch
    },
    computed: {
        hasPaymentMethod() {
            return Object.keys(this.userCardDetails).length > 0
        },
        isApexClubMember() {
            return this.userResource.attributes.credits_subscription_in_good_standing
        },
        bookItButtonDisabled() {
            return this.creatingReservation
            || this.selectedCalendarBecomesUnavailable
            || this.racersQtyExceedsAvail
            || this.remainingCalendarsQtyToBeSelected !== 0
        },
        shouldShowReservationBooking() {
            return this.hasPaymentMethod || this.skipPayment
        },
        suggestedSize() {
            if(!this.shouldShowReservationBooking) {
                return 'md'
            }

            return 'lg'
        },
        racersQtyExceedsAvail() {
            return this.totalCalendarsAvailable < this.racersQty
        },
        remainingCalendarsQtyToBeSelected() {
            return (this.racersQty - 1) - this.otherCalendarsSelected.length
        },
        otherCalendarsAvail() {
            return this.calendarsAvailableForReservationSlot.filter((calendar) => calendar.id != this.calendarId)
        },
        otherCalendarsCostCents() {
            return sum(this.otherCalendarsSelected.map((calendar) => calendar.costCents()))
        },
        totalCalendarsAvailable() {
            return this.calendarsAvailableForReservationSlot.length
        },
    },
    mixins: [messages],
    props: {
        reservationResource: {
            type: ReservationResource,
            required: true
        },
        racers: {
            type: Number,
            default: 1
        }
    },
    watch: {
        racersQty() {
            this.selectOtherCalendarsToFillRacersQty()
        },
        otherCalendarsSelected() {
            this.updateTotalCost()
        },
        shouldShowReservationBooking() {
            this.$emit('size', this.suggestedSize)
        }
    },
    data() {
        return {
            loading: true,
            selectedCalendarBecomesUnavailable: false,
            creatingReservation: false,
            userResource: null,
            imageSrc: null,
            calendarId: null,
            calendarName: null,
            calendarTypeName: null,
            calendarCostCents: '0',
            totalCostCents: '0',
            totalCreditsUsing: '0',
            userCardDetails: {},
            skipPayment: false,
            slotValue: null,
            racersQty: this.racers,
            voucherId: '',
            voucherResource: null,
            voucherEntryVisible: false,
            calendarsAvailableForReservationSlot: [],
            otherCalendarsSelected: []
        }
    },
    methods: {
        async getRelated() {
            this.userResource = await this.$store.dispatch('getUserResource')
            this.imageSrc = await this.reservationResource.resolveAttribute('calendar.image.source')
            this.slotValue = await this.reservationResource.resolveAttribute('slot')
            this.calendarId = await this.reservationResource.resolveAttribute('calendar.id')
            this.calendarName = await this.reservationResource.resolveAttribute('calendar.name')
            this.calendarTypeName = await this.reservationResource.resolveAttribute('calendar.type_name')
            this.calendarTypeDescription = await this.reservationResource.resolveAttribute('calendar.type_description')
            this.calendarCostCents = await this.reservationResource.resolveAttribute('price_cents')
            this.calendarCostUnit = await this.reservationResource.resolveAttribute('calendar.cost_unit')
            this.calendarDescription = await this.reservationResource.resolveAttribute('calendar.description')
            this.userCardDetails = await this.userResource.getCard()
        },
        onCardAdded(result) {
            this.userCardDetails = result.card
        },
        async onApexClubRegister(credits) {
            this.$refs.apexClubModal.hide()
            this.userResource.attributes.credits_balance = credits
            this.userResource.attributes.credits_subscription_in_good_standing = true
            await this.updateTotalCost()
        },
        async getOtherAvailableCalendars() {
            this.calendarsAvailableForReservationSlot = await CalendarResource.hasSlotAvailableAll(this.slotValue)
        },
        selectOtherCalendarsToFillRacersQty() {
            let otherCalsToSelectCount = this.racersQty - 1
            let others = this.otherCalendarsAvail.slice(0, otherCalsToSelectCount)
            this.otherCalendarsSelected = others
        },
        async checkCalendars() {
            let availCalendars = await CalendarResource.hasSlotAvailableAll(this.slotValue)
            let availIds = availCalendars.map((calendar) => calendar.id)
            
            for(let key in this.otherCalendarsSelected) {
                let calendar = this.otherCalendarsSelected[key]

                if(!~availIds.indexOf(calendar.id)) {
                    this.selectedCalendarBecomesUnavailable = true
                    return false
                }
            }

            return true
        },
        async updateTotalCost() {
            let amount = sum([Number(this.calendarCostCents), this.otherCalendarsCostCents])
            let creditsUsing = 0

            if(this.voucherResource) {
                amount = await this.voucherResource.quoteReservationOrder(amount)
            }

            if(this.isApexClubMember) {
                let result = await ChargeResource.finalQuote(amount)
                amount = result.amount_cents
                creditsUsing = result.credits_using
            }
            
            this.totalCostCents = amount
            this.totalCreditsUsing = creditsUsing
        },
        async addVoucher() {
            try {
                this.voucherResource = await VoucherResource.detail(this.voucherId.toUpperCase())
                await this.updateTotalCost()
            } catch(e) {
                if(e.response?.status > 399 && e.response?.status < 500) {
                    this.addMessage('warning', 'The discount code you entered was not found or has already been used')
                    await this.removeVoucher()
                } else {
                    throw e
                }
            }
        },
        async removeVoucher() {
            this.voucherId = ''
            this.voucherResource = null
            await this.updateTotalCost()
        },
        async createReservation() {
            try {
                this.creatingReservation = true

                if(!await this.checkCalendars()) {
                    return false
                }

                await this.reservationResource.save()
                await Promise.all(this.otherCalendarsSelected.map((calendar) => calendar.createReservation({ slot: this.slotValue }).save()))

                if(this.voucherResource) {
                    await ChargeResource.addVoucherToAccount(this.voucherResource.id)
                }

                await ChargeResource.captureOutstanding()
                await this.$store.dispatch('getUserResource')
                this.$emit('success', this.reservationResource)
            } catch(e) {
                this.$analytics.track('exception_during_booking', { message: e.message })
                throw this.axiosExtract(e)
            } finally {
                this.creatingReservation = false
            }
        }
    },
    async mounted() {
        await this.getRelated()
        await this.getOtherAvailableCalendars()
        await this.updateTotalCost()
        
        if(this.racersQty > 0) {
            this.selectOtherCalendarsToFillRacersQty()
        }

        this.$emit('size', this.suggestedSize)

        this.loading = false
    },
}
</script>

<template>
    <div class="reservation-book-body">
        <b-row v-if="!loading">
            <b-col v-if="hasPaymentMethod || skipPayment">
                <b-card header="Detalles de la reserva" header-tag="h5">
                    <b-row>
                        <b-col>
                            <dl class="row mb-0">
                                <dt class="col-sm-3">Fecha</dt>
                                <dd class="col-sm-9">{{ reservationResource }}</dd>
                                <dt class="col-sm-3">Apex Club</dt>
                                <dd class="col-sm-9">
                                    <span v-if="isApexClubMember">
                                        <strong><b-avatar icon="currency-exchange" size="1em"></b-avatar> {{ userResource.attributes.credits_balance }}</strong> créditos restantes de {{ $appConfig('APEX_CLUB_NAME', 'Apex Club') }}
                                    </span>
                                    <span v-else>
                                        <a href="javascript:{}" @click="$refs.apexClubModal.show()">Registrarse</a> <small class="text-muted ml-3"><b-icon icon="arrow-left" animation="cylon" /> <a href="javascript:{}" @click="$refs.apexClubModal.show()">Ahorra un 20 % ahora</a></small>
                                    </span>
                                </dd>
                                <dt class="col-sm-3">Pilotos</dt>
                                <dd class="col-sm-9"><b-form-spinbutton v-model="racersQty" size="sm" inline /> <small><a href="javascript:{}" @click="voucherEntryVisible = true" v-if="!voucherEntryVisible">¿Tienes un código de descuento?</a></small></dd>
                            </dl>
                            <dl class="row mb-0" v-if="voucherEntryVisible">
                                <dt class="col-sm-3">Código de descuento</dt>
                                <dd class="col-sm-9">
                                    <span v-if="this.voucherResource" class="text-success"><b-icon icon="coin" /> <strong>{{ this.voucherResource }}</strong> aplicado — <a href="javascript:{}" @click="removeVoucher">eliminar</a></span>
                                    <b-input-group v-else>
                                        <b-form-input v-model="voucherId" size="sm" class="col-md-3 text-uppercase"></b-form-input>
                                        <b-input-group-append>
                                            <b-button variant="outline-primary" size="sm" @click="addVoucher" :disabled="!voucherId">Aplicar descuento</b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                </dd>
                            </dl>
                            <div v-if="racersQty > 1 && !racersQtyExceedsAvail" class="mb-3">
                                <hr />
                                <strong>Otros simuladores disponibles a esta hora:</strong>
                                <b-form-checkbox-group v-model="otherCalendarsSelected" v-if="otherCalendarsAvail.length > 0" class="mt-3">
                                    <b-list-group>
                                        <b-list-group-item v-for="(calendar, i) in otherCalendarsAvail" :key="i">
                                            <b-form-checkbox :value="calendar" :disabled="!~otherCalendarsSelected.indexOf(calendar) && remainingCalendarsQtyToBeSelected < 1"><strong>{{ calendar }} ({{ calendar.get('type_name')}})</strong> <b-badge variant="success">{{ currencySymbol() }}{{ moneyFormat(calendar.costCents()) }} {{ calendar.attributes.cost_unit }}</b-badge></b-form-checkbox>
                                        </b-list-group-item>
                                    </b-list-group>
                                </b-form-checkbox-group>
                            </div>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <b-alert variant="success" :show="totalCreditsUsing > 0">Canjeando <strong>{{ totalCreditsUsing }} créditos de Apex Club</strong> para reservar {{ racersQty === 1 ? '¡este simulador!' : `¡estos ${racersQty} simuladores!` }}</b-alert>
                            <b-alert variant="info" :show="totalCostCents > 0">Se realizará un cargo de <strong>{{ currencySymbol() }}{{ moneyFormat(totalCostCents) }}</strong> en tu <strong>{{ userCardDetails.brand }} terminada en {{ userCardDetails.last4 }}</strong></b-alert>
                            <b-alert variant="warning" :show="remainingCalendarsQtyToBeSelected != 0 && !racersQtyExceedsAvail"><b-icon icon="exclamation-triangle" /> Debes seleccionar {{ remainingCalendarsQtyToBeSelected }} {{ remainingCalendarsQtyToBeSelected == 1 ? 'simulador' : 'simuladores' }} más arriba para continuar</b-alert>
                            <b-alert variant="danger" :show="racersQtyExceedsAvail"><b-icon icon="exclamation-triangle" /> Lo sentimos, no hay {{ racersQty }} simuladores disponibles para esta franja horaria</b-alert>
                            <b-alert variant="danger" :show="selectedCalendarBecomesUnavailable"><b-icon icon="exclamation-triangle" /> Lo sentimos, uno de los simuladores que seleccionaste ya no está disponible</b-alert>
                            <messages />
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col>
                            <small class="text-muted"><terms-of-service-link /></small>
                            <b-button variant="primary" class="mt-3" @click="createReservation" :disabled="bookItButtonDisabled">{{ racersQty === 1 ? '¡Reservar simulador!' : `¡Reservar ${racersQty} simuladores!` }}</b-button>
                        </b-col>
                    </b-row>
                </b-card>

                <h4 class="mt-3">Resumen de la carrera</h4>
                <p>{{ racersQty }} {{ racersQty == 1 ? 'piloto' : 'pilotos' }}, {{ racersQty }} {{ racersQty == 1 ? 'simulador' : 'simuladores' }} {{ calendarCostUnit }}. Coste total {{ currencySymbol() }}{{ moneyFormat(totalCostCents) }}<span v-if="totalCreditsUsing > 0">(Usando {{ totalCreditsUsing }} créditos de Apex Club)</span></p>

                <b-card
                    :header="racersQty > 1 ? `Piloto n.º 1 (tú) - ${calendarName}` : calendarName"
                    border-variant="primary"
                    header-bg-variant="primary"
                    header-text-variant="white"
                    header-tag="h5"
                    class="mt-3">
                    <b-row>
                        <b-col md="4">
                            <img :src="imageSrc" />
                        </b-col>
                        <b-col>
                            <dl class="row mb-0">
                                <dt class="col-sm-3">Tipo</dt>
                                <dd class="col-sm-9">{{ calendarTypeName }}</dd>
                                <dt class="col-sm-3">Tarifa</dt>
                                <dd class="col-sm-9"><b-badge variant="success"><span v-if="isApexClubMember"><b-icon icon="currency-exchange" /> {{ centsToCredits(calendarCostCents) }} / </span>{{ currencySymbol() }}{{ moneyFormat(calendarCostCents) }} {{ calendarCostUnit }}</b-badge></dd>
                                <dt class="col-sm-3">Descripción</dt>
                                <dd class="col-sm-9"><span v-if="calendarDescription" v-html="calendarDescription + '<br />'"></span>{{ calendarTypeDescription }}</dd>
                            </dl>
                        </b-col>
                    </b-row>
                </b-card>

                <b-card
                    v-for="(calendar, i) in otherCalendarsSelected"
                    :key="i"
                    :header="`Piloto n.º ${i+2} - ${calendar}`"
                    border-variant="primary"
                    header-bg-variant="primary"
                    header-text-variant="white"
                    header-tag="h5"
                    class="mt-3">
                    <b-row>
                        <b-col md="4">
                            <related-image :for="calendar" />
                        </b-col>
                        <b-col>
                            <dl class="row mb-0">
                                <dt class="col-sm-3">Tipo</dt>
                                <dd class="col-sm-9">{{ calendar.attributes.type_name }}</dd>
                                <dt class="col-sm-3">Tarifa</dt>
                                <dd class="col-sm-9"><b-badge variant="success"><span v-if="isApexClubMember"><b-icon icon="currency-exchange" /> {{ centsToCredits(calendar.attributes.cost_cents) }} / </span>{{ currencySymbol() }}{{ moneyFormat(calendar.attributes.cost_cents) }} {{ calendar.attributes.cost_unit }}</b-badge></dd>
                                <dt class="col-sm-3">Descripción</dt>
                                <dd class="col-sm-9"><span v-if="calendar.attributes.description" v-html="calendar.attributes.description + '<br />'"></span>{{ calendar.attributes.type_description }}</dd>
                            </dl>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
            <b-col v-else>
                <payment-method cols-lg="12" :user-resource="userResource" @success="onCardAdded">
                    <b-alert variant="success" show><b-icon icon="lock-fill" /> Aún no se realizará ningún cargo en tu tarjeta</b-alert>
                    <!-- <template slot="after-submit-button-text">
                        <b-button variant="link" @click="skipPayment = true">skip this step</b-button>
                    </template> -->
                </payment-method>
            </b-col>
        </b-row>

        <b-row v-else>
            <b-col>
                <b-skeleton :width="Math.floor(Math.random() * (100 - 60 + 1) + 60) + '%'" v-for="i in 10" :key="i" />
            </b-col>
        </b-row>

        <b-modal ref="apexClubModal" size="md" hide-footer no-fade centered>
            <apex-club-pitch @success="onApexClubRegister" />
        </b-modal>
    </div>
</template>

<style lang="scss">
.reservation-book-body {
    img {
        max-width: 100%;
    }
}
</style>
